<script>
import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import CreateUnavailabilityModal from './createUnavailabilityModal.vue';
import DeleteConfirmationModal from './deleteConfirmationModal.vue';

export default {
  props: {
    staffId: {
      type: String
    }
  },
  components: {
    CreateUnavailabilityModal,
    DeleteConfirmationModal
  },
  data() {
    return {
      unavailabilityList: [],
      isCreating: false,
      deleteObject: null,
    };
  },
  methods: {
    ...mapActions('appointments', [
      'fetchStaffUnavailability',
      'deleteStaffUnavailability',
    ]),

    async handleDelete(date) {
      try {
        await this.deleteStaffUnavailability({
          staffId: this.staffId,
          date: date
        });
        useAlert(this.$t("BRANCHES.VIEW_STAFF_UNAVAILABILITY.DELETE_SUCCESS"))
        this.deleteObject = null;
      } catch (err) {
        console.log(err);
        useAlert(err);
      } finally {
        this.isCreating = false;
        this.refreshList();
      }
    },

    handleCreate() {
      this.isCreating = true;
    },

    resetCreating() {
      this.isCreating = false;
      this.refreshList();
    },

    async refreshList() {
      try {
        this.unavailabilityList = await this.fetchStaffUnavailability(this.staffId);
      } catch (err) {
        console.error(err);
        useAlert(err); // TODO: i18n this if it's a string key
      }
    },

    formatTime(timeString) {
      if (!timeString || timeString === this.$t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.START_TIME') || timeString === this.$t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.END_TIME')) return timeString;

      try {
        const timeParts = timeString.split(':');
        let hours = parseInt(timeParts[0], 10);
        const minutes = timeParts[1];
        const ampm = hours >= 12 ? this.$t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.PM') : this.$t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.AM');

        hours = hours % 12;
        hours = hours ? hours : 12;

        return `${hours}:${minutes} ${ampm}`;
      } catch (e) {
        return timeString;
      }
    },
  },
  async mounted() {
    await this.refreshList();
  },
};
</script>

<template>
  <div class="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-md">
    <!-- Modal -->
    <div 
      v-if="isCreating" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <CreateUnavailabilityModal
        :resetCreating="resetCreating" 
        :staffId="staffId" 
        :refreshList="refreshList" 
      />
    </div>

    <div
      v-if="deleteObject"
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <DeleteConfirmationModal         
        :deleteName="$t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.DELETE_NAME')"
        @confirm-delete="handleDelete(deleteObject)"
        @close="deleteObject = null"
      />
    </div>

    <!-- Header -->
    <div class="flex justify-between items-center mb-4 flex-wrap gap-2">
      <h1 class="text-lg font-semibold text-gray-900">
        {{ $t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.TITLE') }}
      </h1>
      <woot-button
        variant="smooth"
        color-scheme="primary"
        icon="plus-sign"
        size="small"
        @click="isCreating = true"
      >
      {{ $t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.CREATE') }}
      </woot-button>
    </div>

    <!-- Unavailability Items -->
    <div 
      v-if="unavailabilityList.length !== 0" 
      v-for="unavailability in unavailabilityList" 
      :key="unavailability.id" 
      class="flex justify-between items-center p-3 border-b last:border-none"
    >
      <div class="flex flex-col gap-1 p-2">
        <h1 class="text-lg font-semibold text-gray-800 dark:text-gray-100">
          {{ $t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.DATE_LABEL') }} {{ unavailability.unavailable_date }}
        </h1>
        <p class="text-sm text-gray-600 dark:text-gray-300">
          {{ $t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.REASON_LABEL') }} {{ unavailability.reason || '-' }}
        </p>
        <p class="text-sm text-gray-600 dark:text-gray-300">
          {{ $t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.TIME_LABEL') }} {{ formatTime(unavailability.start_time) }} - {{ formatTime(unavailability.end_time) }}
        </p>
      </div>

      <!-- Delete Button -->
      <button 
        @click="deleteObject = unavailability.unavailable_date"
        class="px-3 py-1 text-sm font-medium text-red-600 border border-red-600 rounded-md hover:bg-red-600 hover:text-white transition"
      >
      {{ $t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.DELETE') }}
      </button>
    </div>

    <!-- Empty State -->
    <div v-if="unavailabilityList.length === 0" class="text-center text-gray-500 dark:text-gray-400">
      <p>{{ $t('BRANCHES.VIEW_STAFF_UNAVAILABILITY.NO_UNAVAILABILITY') }}</p>
    </div>
  </div>
</template>
